/**
 * Define all available feature flags here.
 * Add new flags by simply adding them to this type - no database changes needed.
 */
export type FeatureFlagKey =
  | "clients"
  | "drivers"
  | "tractors"
  | "trailers"
  | "locations"
  | "billTos"
  | "dispatchFinder"
  | "dispatchDriverAssignments"
  | "dispatchTrips"
  | "newReports"
  | "useNewDriverPages"
  | "useNewClientPages"
  | "useNewTractorPages"
  | "useNewTrailerPages"
  | "emailFeatures";

/**
 * Feature flags as a record type for flexibility
 */
export type FeatureFlags = Record<FeatureFlagKey, boolean>;

/**
 * Default values for all feature flags
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  clients: false,
  drivers: false,
  tractors: false,
  trailers: false,
  locations: false,
  billTos: false,
  dispatchFinder: false,
  dispatchDriverAssignments: false,
  dispatchTrips: false,
  newReports: false,
  useNewDriverPages: false,
  useNewClientPages: false,
  useNewTractorPages: false,
  useNewTrailerPages: false,
  emailFeatures: false,
};

/**
 * All available feature flag keys
 */
export const ALL_FEATURE_FLAG_KEYS: FeatureFlagKey[] = Object.keys(
  DEFAULT_FEATURE_FLAGS
) as FeatureFlagKey[];

export interface FeatureFlagInfo {
  key: FeatureFlagKey;
  label: string;
  description: string;
}

/**
 * Information about each feature flag for UI display.
 * Add new flags here when adding them to FeatureFlagKey.
 */
export const FEATURE_FLAG_INFO: FeatureFlagInfo[] = [
  {
    key: "clients",
    label: "Clients",
    description: "Enable new client management features",
  },
  {
    key: "drivers",
    label: "Drivers",
    description: "Enable new driver management features",
  },
  {
    key: "tractors",
    label: "Tractors",
    description: "Enable new tractor management features",
  },
  {
    key: "trailers",
    label: "Trailers",
    description: "Enable new trailer management features",
  },
  {
    key: "locations",
    label: "Locations",
    description: "Enable new location management features",
  },
  {
    key: "billTos",
    label: "Bill Tos",
    description: "Enable new bill-to management features",
  },
  {
    key: "dispatchFinder",
    label: "Dispatch Finder",
    description: "Enable dispatch finder features",
  },
  {
    key: "dispatchDriverAssignments",
    label: "Dispatch Driver Assignments",
    description: "Enable dispatch driver assignment features",
  },
  {
    key: "dispatchTrips",
    label: "Dispatch Trips",
    description: "Enable dispatch trip features",
  },
  {
    key: "newReports",
    label: "New Reports",
    description: "Enable new reporting features",
  },
  {
    key: "useNewDriverPages",
    label: "Use New Driver Pages",
    description: "Route driver list to new driver detail pages",
  },
  {
    key: "useNewClientPages",
    label: "Use New Client Pages",
    description: "Route client list to new client detail pages",
  },
  {
    key: "useNewTractorPages",
    label: "Use New Tractor Pages",
    description: "Route tractor list to new tractor detail pages",
  },
  {
    key: "useNewTrailerPages",
    label: "Use New Trailer Pages",
    description: "Route trailer list to new trailer detail pages",
  },
  {
    key: "emailFeatures",
    label: "Email Features",
    description: "Enable sending emails from new dispatch related pages / notes",
  }
];

/**
 * API response model from the server
 */
export interface FeatureFlagsApiResponse {
  flags: Record<string, boolean>;
}
